import { useDraggable } from '@dnd-kit/core';
import {
  EllipsisVerticalIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/20/solid';
import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import useCloseContextMenuOnDragging from 'hooks/use-close-context-menu-on-dragging';

type DndKitDraggableItemProps = {
  id: string;
  label: string;
  type:
    | 'new-space'
    | 'new-condenser'
    | 'new-ptac-unit'
    | 'new-condenser-ground'
    | 'new-condenser-wall'
    | 'new-condenser-roof'
    | 'new-air-handler'
    | 'new-air-handler-high-wall'
    | 'new-air-handler-low-wall'
    | 'new-air-handler-floor-ceiling'
    | 'new-air-handler-ceiling-cassette'
    | 'new-air-handler-ceiling-cassette-slim-fit'
    | 'new-air-handler-concealed-duct'
    | 'new-air-handler-ducted'
    | 'new-lineset'
    | 'new-window-small'
    | 'new-window-medium'
    | 'new-window-large'
    | 'new-door-wood'
    | 'new-door-metal'
    | 'new-door-pet'
    | 'new-door-glass-single'
    | 'new-door-glass-double'
    | 'new-door-garage-single'
    | 'new-door-garage-double'
    | 'new-appliance'
    | 'new-skylight-small'
    | 'new-skylight-medium'
    | 'new-skylight-large';
  itemId?: number;
  disabled?: boolean;
  onDoubleClick?: () => void;
  color?: 'blue' | 'purple' | 'yellow' | 'green' | 'cyan' | 'orange' | 'red';
  image?: string;
  description?: string;
  size?: 'default' | 'large';
  tooltip?: string;
  warning?: string;
};

export default function DndKitDraggableItem({
  id,
  label,
  type,
  itemId,
  disabled,
  onDoubleClick,
  color = 'blue',
  image,
  description,
  size = 'default',
  tooltip,
  warning
}: DndKitDraggableItemProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id,
      disabled,
      data: {
        id,
        label,
        type,
        itemId
      }
    });
  useCloseContextMenuOnDragging(isDragging);

  const getOpacity = () => {
    if (isDragging) return 0;
    if (disabled) return 0.5;
    return 1;
  };

  const style = {
    transform:
      transform && !isDragging
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
    opacity: getOpacity()
  };

  const isLarge = size === 'large';

  const classes = clsx(
    'flex gap-1 items-center font-semibold rounded-md pr-5 border-2 py-1 relative',
    isLarge ? 'text-base gap-2' : 'text-sm',
    !image && 'min-h-[40px]',
    isDragging && 'cursor-grabbing',
    disabled ? 'cursor-not-allowed' : 'cursor-grab',
    color === 'blue' && 'bg-blue-100 border-blue-300',
    color === 'purple' && 'bg-purple-100 border-purple-300',
    color === 'yellow' && 'bg-yellow-100 border-yellow-300',
    color === 'green' && 'bg-green-100 border-green-300',
    color === 'cyan' && 'bg-cyan-100 border-cyan-300',
    color === 'orange' && 'bg-orange-100 border-orange-300',
    color === 'red' && 'bg-red-100 border-red-300'
  );

  return (
    <button
      type='button'
      ref={setNodeRef}
      id={id}
      style={style}
      className={classes}
      onDoubleClick={onDoubleClick}
      {...attributes}
      {...listeners}
    >
      <div className='flex shrink-0'>
        <EllipsisVerticalIcon className='h-4 w-4 translate-x-3' />
        <EllipsisVerticalIcon className='h-4 w-4 -translate-x-2' />
      </div>
      {image && (
        <img
          src={image}
          alt={label}
          className={clsx(
            'shrink-0 object-contain',
            isLarge ? 'h-24 w-24' : 'h-12 w-12'
          )}
        />
      )}
      <div className='flex flex-col text-left'>
        <span>{label}</span>
        {description && (
          <span
            className={clsx(
              'font-normal text-gray-600',
              isLarge ? 'text-sm' : 'text-xs'
            )}
          >
            {description}
          </span>
        )}
      </div>
      {warning && !disabled && (
        <ExclamationTriangleIcon className='absolute right-2 top-2 h-5 w-5 text-amber-500' />
      )}
      {(tooltip || (warning && !disabled)) && (
        <Tooltip text={tooltip ?? warning ?? ''} />
      )}
    </button>
  );
}
